import { useColorModeValue, useToast } from "@chakra-ui/react";
import { useFormikContext } from "formik";
import React from "react";
import { QuoteApi } from "../../api/QuoteApi";
import useUser from "../../hooks/useUser";
import { SelectItem } from "../../lib/types/SelectItem";
import { UserAccount } from "../../lib/types/UserAccountType";
import { isElevatedUser } from "../../lib/UserPermissions";
import ComboBox from "../form-fields/ComboBox";

export default function QuoteOwnerOverride() {
  const { sfProfile, role } = useUser();
  const { setFieldValue } = useFormikContext();
  const _isMounted = React.useRef(true);
  const toast = useToast();
  const [loading, setLoading] = React.useState<boolean>();
  const [accounts, setAccounts] = React.useState<UserAccount[]>([]);
  const [selectedAccount, setSelectedAccount] =
    React.useState<SelectItem | null>(null);
  const toastVariant = useColorModeValue("subtle", "solid");

  const brokerItems: SelectItem[] = React.useMemo(() => {
    if (sfProfile?.brokers) {
      return sfProfile?.brokers.reduce((filtered, broker) => {
        if (broker.accountid) {
          const brokerItem = {
            label: broker.name,
            value: broker.username,
          };
          filtered.push(brokerItem);
        }
        return filtered;
      }, [] as SelectItem[]);
    }
    return [];
  }, [sfProfile?.brokers]);

  const accountItems: SelectItem[] = React.useMemo(() => {
    return accounts.map((account) => {
      return {
        label: `${account.firstName} ${account.lastName}`,
        value: account.username,
      };
    });
  }, [accounts]);

  const handleOwnerOverride = (brokerItem: SelectItem | null) => {
    setSelectedAccount(brokerItem);
    setFieldValue("username", brokerItem?.value);
  };

  const fetchUsers = React.useCallback(() => {
    setLoading(true);

    QuoteApi.getQuoteUsers()
      .then((res) => {
        const activeUsers = res.data.map((x) => {
          return x;
        });
        if (_isMounted.current) {
          setAccounts(activeUsers);
        }
      })
      .catch((err) => {
        toast({
          title: "Unable to Load Users",
          description: "An error has occurred when attempting to fetch users.",
          status: "error",
          variant: toastVariant,
          isClosable: true,
          position: "top-right",
        });
      });
    setLoading(false);
  }, [toast, toastVariant]);

  React.useEffect(() => {
    return () => {
      _isMounted.current = false;
    };
  }, []);

  React.useEffect(() => {
    fetchUsers();
  }, [fetchUsers, loading]);

  return (
    <>
      {isElevatedUser(role) ? (
        <ComboBox
          items={accountItems}
          label="Change Ownership"
          onSelect={handleOwnerOverride}
          itemSelected={selectedAccount}
          placeholder={loading ? "Loading..." : "Select a User..."}
        />
      ) : (
        <ComboBox
          items={brokerItems}
          label="Change Ownership"
          onSelect={handleOwnerOverride}
          itemSelected={selectedAccount}
          placeholder="Select a Broker..."
        />
      )}
    </>
  );
}
